import { Button, Empty, Input, Skeleton, Typography } from "antd";
import {
  ArrowRightOutlined,
  LinkOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { errorMsg } from "../../Utils/Utils";
import { toCompareNplSuppliers } from "./Api";

const PROVIDER_LABELS = {
  steq: "STE Q",
  cdg: "CDG",
  fad: "FAD",
};

const PROVIDER_ORDER = ["steq", "cdg", "fad"];

const PROVIDER_THEMES = {
  mixed: {
    label: "mixed",
    bg: "#eff6ff",
    text: "#3b82f6",
    soft: "#bfdbfe",
  },
  steq: {
    label: "STE Q",
    chip: "steq",
    bg: "#ecfdf5",
    text: "#047857",
    soft: "#a7f3d0",
  },
  cdg: {
    label: "CDG",
    chip: "cdg",
    bg: "#fffbeb",
    text: "#b45309",
    soft: "#fde68a",
  },
  fad: {
    label: "FAD",
    chip: "fad",
    bg: "#e0f2fe",
    text: "#0369a1",
    soft: "#bae6fd",
  },
};

const providerTheme = (key) =>
  PROVIDER_THEMES[key] || {
    label: PROVIDER_LABELS[key] || key,
    chip: key.toLowerCase(),
    bg: "#f1f5f9",
    text: "#334155",
    soft: "#cbd5e1",
  };

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  const s = String(v).trim().replace(/\s+/g, "");
  const m = s.match(/[-+]?\d[\d.,]*/);
  if (!m) return NaN;
  let t = m[0];
  if (t.includes(",") && t.includes(".")) t = t.replace(/,/g, "");
  else if (t.includes(",")) t = t.replace(".", "").replace(",", ".");
  const n = parseFloat(t);
  return isNaN(n) ? NaN : n;
};

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (v === null || v === undefined || v === "") return undefined;
  const s = String(v).trim().toLowerCase();
  if (
    [
      "0",
      "false",
      "non",
      "no",
      "indisponible",
      "out",
      "rupture",
      "epuise",
      "épuisé",
    ].includes(s)
  )
    return false;
  if (
    [
      "1",
      "true",
      "oui",
      "yes",
      "disponible",
      "en stock",
      "in stock",
      "stock",
      "present",
      "présent",
    ].includes(s)
  )
    return true;
  if (/^\d+$/.test(s)) return Number(s) > 0;
  return /dispon|stock|oui|yes|true/i.test(s);
};

const normalizeOffer = (offer) => {
  if (!offer || typeof offer !== "object" || Array.isArray(offer)) return null;
  const ref = offer.supplierReference || offer.reference || "";
  if (!ref) return null;
  const available = toBool(offer.available);
  const availabilityText = offer.availability ? String(offer.availability) : "";
  return {
    ref: String(ref).trim(),
    designation: offer.description ? String(offer.description).trim() : "",
    brand: offer.brand ? String(offer.brand).trim() : "",
    code: offer.reference != null ? String(offer.reference) : "",
    price: toNumber(
      offer.priceHtText != null ? offer.priceHtText : offer.priceHt,
    ),
    availability: {
      available,
      pending:
        available === false &&
        /arrivage|devis|attente|commande|prochaine|back.?order/i.test(
          availabilityText,
        ),
      text: availabilityText,
    },
    link:
      offer.imageUrl &&
      typeof offer.imageUrl === "string" &&
      /^(https?:\/\/|www\.)/i.test(offer.imageUrl)
        ? offer.imageUrl
        : "",
  };
};

const collectOffers = (provider) => {
  if (!provider || !provider.ok || !Array.isArray(provider.offers)) return [];
  const out = [];
  const seen = new Set();
  provider.offers.forEach((offer) => {
    const n = normalizeOffer(offer);
    if (!n) return;
    const mid = n.ref.toUpperCase();
    if (seen.has(mid)) return;
    seen.add(mid);
    out.push(n);
  });
  return out;
};

const buildMergedRows = (providers) => {
  const map = new Map();
  const keys = PROVIDER_ORDER.concat(
    Object.keys(providers).filter((k) => !PROVIDER_ORDER.includes(k)),
  );
  keys.forEach((key) => {
    const provider = providers[key];
    collectOffers(provider).forEach((r) => {
      const mid = r.ref.toUpperCase();
      let merged = map.get(mid);
      if (!merged) {
        merged = {
          ref: r.ref,
          designation: r.designation,
          bySupplier: {},
        };
        map.set(mid, merged);
      }
      if (!merged.designation && r.designation)
        merged.designation = r.designation;
      merged.bySupplier[key] = { ...r };
    });
  });
  return Array.from(map.values());
};

const findDateValue = (obj) => {
  if (!obj) return null;
  if (Array.isArray(obj)) {
    if (obj.length && typeof obj[0] === "object" && obj[0])
      return findDateValue(obj[0]);
    return null;
  }
  if (typeof obj !== "object") return null;
  for (const k of Object.keys(obj)) {
    if (!/updated|synchron|timestamp|created|^date$|derni_re_mise/i.test(k))
      continue;
    const v = obj[k];
    if (typeof v === "string" && v) {
      const d = new Date(v);
      if (!isNaN(d.getTime())) return d;
    }
  }
  return null;
};

const providerUpdatedAt = (provider) =>
  findDateValue(provider) ||
  findDateValue(provider && provider.offers && provider.offers[0]);

const globalUpdatedAt = (result) =>
  findDateValue(result) ||
  findDateValue(result && result.providers && result.providers.steq) ||
  findDateValue(
    result && result.providers && Object.values(result.providers)[0],
  ) ||
  new Date();

const formatDateTime = (d) => {
  if (!d || isNaN(new Date(d).getTime())) return "";
  const date = new Date(d);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const hours = date.getHours();
  const half = hours >= 12 ? "PM" : "AM";
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()} ${h12}:${minutes} ${half}`;
};

const formatPrice = (v) => {
  const n = toNumber(v);
  if (isNaN(n) || n < 0) return null;
  return `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(n)} DT`;
};

const cellStatus = (row) => {
  if (!row) return { present: false, kind: "", text: "" };
  const a = row.availability || {};
  const hasPrice = Number.isFinite(row.price) && row.price >= 0;
  let kind;
  if (a.available === false) kind = a.pending ? "pending" : "low";
  else if (a.available === true) kind = "ok";
  else if (hasPrice) kind = "ok";
  else if (a.text) kind = "pending";
  else kind = "ok";
  return { present: true, kind, text: a.text || "" };
};

const STATUS_LABEL = { ok: "Present", pending: "Present", low: "Present" };

const STATUS_CLASS = {
  ok: "npl-sup-status--ok",
  pending: "npl-sup-status--pending",
  low: "npl-sup-status--pending",
  none: "npl-sup-status--none",
};

const SupplierStatus = ({ row }) => {
  const { present, kind } = cellStatus(row);
  if (!present)
    return <span className={"npl-sup-status " + STATUS_CLASS.none}>Non</span>;
  return (
    <span
      className={"npl-sup-status " + (STATUS_CLASS[kind] || STATUS_CLASS.ok)}
    >
      {STATUS_LABEL[kind] || "Present"}
    </span>
  );
};

const PriceCell = ({ row }) => {
  if (!row) return <span className="npl-sup-price-empty">-</span>;
  const fp = formatPrice(row.price);
  if (!fp)
    return (
      <span className="npl-sup-price npl-sup-price--quote">Sur devis</span>
    );
  return <span className="npl-sup-price">{fp}</span>;
};

const SupplierCell = ({ row }) => {
  if (!row)
    return (
      <div className="npl-sup-prov">
        <SupplierStatus row={null} />
      </div>
    );
  const { kind } = cellStatus(row);
  return (
    <div className="npl-sup-prov">
      <SupplierStatus row={row} />
      {row.brand && <span className="npl-sup-brand">{row.brand}</span>}
      {row.code && (
        <span className="npl-sup-meta">
          Code:&nbsp;<b>{row.code}</b>
        </span>
      )}
      {row.availability && row.availability.text && (
        <span
          className={
            "npl-sup-avail " + (kind === "ok" ? "" : "npl-sup-avail--warn")
          }
        >
          {String(row.availability.text).toUpperCase()}
        </span>
      )}
    </div>
  );
};

const LinksCell = ({ row, suppliers }) => {
  return (
    <div className="npl-sup-links">
      {suppliers.map((key) => {
        const theme = providerTheme(key);
        const item = row.bySupplier[key];
        const link = null;
        if (!item)
          return (
            <span key={key} className="npl-sup-link npl-sup-link--off">
              {theme.label}
            </span>
          );
        if (link)
          return (
            <a
              key={key}
              className="npl-sup-link"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span
                className="npl-sup-link-dot"
                style={{ background: theme.text }}
              />
              {theme.label}
              <LinkOutlined style={{ fontSize: 11, opacity: 0.7 }} />
            </a>
          );
        return (
          <span
            key={key}
            className="npl-sup-link npl-sup-link--no"
            title="Lien non disponible"
          >
            <span
              className="npl-sup-link-dot"
              style={{ background: theme.text }}
            />
            {theme.label}
          </span>
        );
      })}
    </div>
  );
};

const Providers = ({ initialRef }) => {
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeProvider, setActiveProvider] = useState("mixed");

  const compare = useCallback(async (ref) => {
    if (!ref) {
      errorMsg("Veuillez entrer une référence");
      return;
    }
    setLoading(true);
    setResult(null);
    setActiveProvider("mixed");
    try {
      const res = await toCompareNplSuppliers(ref);
      console.log({ res });
      setResult(res);
    } catch (e) {
      setResult(null);
      errorMsg(
        e.response?.data?.errors || "Impossible de comparer les fournisseurs",
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (initialRef && initialRef.value && initialRef.value !== reference) {
      setReference(initialRef.value);
      compare(initialRef.value);
    }
  }, [initialRef, compare, reference]);

  const providers = useMemo(() => (result && result.providers) || {}, [result]);
  const supplierKeys = useMemo(
    () =>
      PROVIDER_ORDER.concat(
        Object.keys(providers).filter((k) => !PROVIDER_ORDER.includes(k)),
      ),
    [providers],
  );

  const providerTotals = useMemo(() => {
    const map = {};
    supplierKeys.forEach((k) => {
      const p = providers[k];
      map[k] = p && p.ok && Array.isArray(p.offers) ? p.offers.length : 0;
    });
    return map;
  }, [supplierKeys, providers]);

  const mergedRows = useMemo(() => buildMergedRows(providers), [providers]);

  const visibleRows =
    activeProvider === "mixed"
      ? mergedRows
      : mergedRows.filter((r) => r.bySupplier[activeProvider]);

  const globalDate = formatDateTime(globalUpdatedAt(result));
  const subtitle = `${mergedRows.length} équivalent${
    mergedRows.length > 1 ? "s" : ""
  }${globalDate ? " - " + globalDate : ""}`;

  const tags = [
    { key: "mixed", label: "mixed" },
    ...supplierKeys.map((k) => ({ key: k, label: PROVIDER_LABELS[k] || k })),
  ];

  const originLine = () =>
    `Recherche: ${String(result && result.reference ? result.reference : "")}`;

  return (
    <div>
      {/* ---------- Recherche ---------- */}
      <div className="npl-card npl-card-pad">
        <div className="npl-section-label" style={{ marginBottom: 14 }}>
          Comparateur de prix fournisseurs
        </div>
        <div className="npl-searchbar">
          <Input
            className="npl-vin-input"
            style={{ flex: 1, minWidth: 260, maxWidth: 480 }}
            prefix={<SearchOutlined className="npl-search-icon" />}
            placeholder="Référence pièce (ex: 46401356)"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            onPressEnter={(e) => compare(e.target.value)}
          />
          <Button
            type="primary"
            className="npl-btn-primary"
            loading={loading}
            onClick={() => compare(reference)}
          >
            Comparer <ArrowRightOutlined style={{ fontSize: 12 }} />
          </Button>
        </div>
      </div>

      <Skeleton active loading={loading} />

      {/* ---------- État vide ---------- */}
      {!loading && result === null && (
        <div className="npl-card">
          <div className="npl-empty-box">
            <Typography.Text type="secondary">
              Entrez une référence pour comparer la disponibilité et les prix
              chez les fournisseurs
            </Typography.Text>
          </div>
        </div>
      )}

      {/* ---------- Résultats ---------- */}
      {!loading && result && (
        <>
          {/* En-tête de page */}
          <div className="npl-sup-header">
            <div>
              <h2 className="npl-sup-title">
                Reference{" "}
                <span className="npl-cell-mono">{result.reference}</span>
              </h2>
              <p className="npl-sup-sub">{subtitle}</p>
            </div>
            <div className="npl-sup-tags">
              {tags.map((t) => {
                const theme = providerTheme(t.key);
                const active = activeProvider === t.key;
                return (
                  <span
                    key={t.key}
                    className={
                      "npl-sup-tag" + (active ? " npl-sup-tag--active" : "")
                    }
                    style={
                      active
                        ? {
                            background: theme.bg,
                            color: theme.text,
                            borderColor: theme.soft,
                            boxShadow: `0 0 0 1px ${theme.soft} inset`,
                          }
                        : undefined
                    }
                    title={
                      t.key === "mixed"
                        ? "Voir toutes les références"
                        : `Filtrer par ${PROVIDER_LABELS[t.key] || t.key}\n${
                            providerTotals[t.key]
                          } référence(s)`
                    }
                    onClick={() => setActiveProvider(t.key)}
                  >
                    {t.label}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Cartes résumé fournisseurs */}
          <div className="npl-sup-cards">
            {supplierKeys.map((key) => {
              const theme = providerTheme(key);
              const provider = providers[key];
              const ok = Boolean(provider && provider.ok);
              const count = providerTotals[key];
              const date = formatDateTime(providerUpdatedAt(provider));
              return (
                <div
                  key={key}
                  className={"npl-sup-card" + (ok ? "" : " npl-sup-card--ko")}
                >
                  <div className="npl-sup-card-head">
                    <span className="npl-sup-card-name">
                      {PROVIDER_LABELS[key] || key}
                    </span>
                    <span
                      className="npl-sup-pill"
                      style={{ background: theme.bg, color: theme.text }}
                    >
                      {theme.chip}
                    </span>
                    <span className="npl-sup-card-count">
                      {count > 0
                        ? count + " ref" + (count > 1 ? "s" : "")
                        : "non disponible"}
                    </span>
                  </div>
                  {!ok && (
                    <div className="npl-sup-card-err">
                      Aucun résultat ou erreur chez ce fournisseur
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tableau comparatif */}
          {mergedRows.length === 0 ? (
            <div className="npl-card">
              <div className="npl-empty-box">
                <Empty description="Aucune référence équivalente trouvée" />
              </div>
            </div>
          ) : (
            <div
              className="npl-card"
              style={{ padding: 0, overflow: "hidden" }}
            >
              {visibleRows.length === 0 ? (
                <div className="npl-empty-box">
                  <Empty description="Aucune référence équivalente chez ce fournisseur" />
                </div>
              ) : (
                <div className="npl-table-wrap npl-sup-table-wrap">
                  <table className="npl-table npl-sup-table">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 190 }}>Référence équivalente</th>
                        <th style={{ minWidth: 150 }}>Désignation</th>
                        {supplierKeys.map((key) => (
                          <th key={key} className="npl-sup-th-prov">
                            {PROVIDER_LABELS[key] || key}
                          </th>
                        ))}
                        {supplierKeys.map((key) => (
                          <th key={key} className="npl-sup-th-price">
                            Prix {PROVIDER_LABELS[key] || key}
                          </th>
                        ))}
                        <th style={{ minWidth: 120 }}>Liens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((row, i) => (
                        <tr key={i}>
                          <td>
                            <div className="npl-cell-mono npl-sup-ref">
                              {row.ref}
                            </div>
                            <div className="npl-sup-cell-sub">
                              {originLine()}
                            </div>
                          </td>
                          <td>
                            <span className="npl-sup-cell-emph">
                              {row.designation || "-"}
                            </span>
                          </td>
                          {supplierKeys.map((key) => (
                            <td key={key}>
                              <SupplierCell row={row.bySupplier[key]} />
                            </td>
                          ))}
                          {supplierKeys.map((key) => (
                            <td key={key} className="npl-sup-td-price">
                              <PriceCell row={row.bySupplier[key]} />
                            </td>
                          ))}
                          <td>
                            <LinksCell row={row} suppliers={supplierKeys} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Providers;
